import ko from "knockout";

declare const fieldDepthMap: Array<IFieldDepth>;
declare const DevExpress: any;

// Drag-reorder for the selections <ul>; splices the bound observableArray to match
ko.bindingHandlers["dxSortableList"] = {
    init: (element, valueAccessor) => {
        const items = valueAccessor();   // the observableArray (e.g. selections.stations)
        new DevExpress.ui.dxSortable(element, {
            dropFeedbackMode: "push",
            onReorder: (e: any) => {
                const list = items();
                const moved = list[e.fromIndex];
                items.splice(e.fromIndex, 1);
                items.splice(e.toIndex, 0, moved);
            }
        });
    }
};

enum BuilderStep {
    Stations = 1,
    Interval = 2,
    Date = 3,
    Fields = 4,
    Custom = 5
}

enum ReportDateTypeEnum {
    LatestObservation = 12,
    Count = 1,
    Last24Hours = 10,
    Last48Hours = 11,
    SevenDays = 2,
    ThirtyDays = 3,
    SixtyDays = 4,
    CurrentMonth = 5,
    LastMonth = 7,
    CurrentYear = 6,
    LastYear = 9,
    Season = 8,
    DateRange = 99
}

class Constraints {
    private readonly selections: Selections;

    singleStation: ko.Computed<boolean>;
    singleField: ko.Observable<boolean> = ko.observable(false);
    allowAllFields: ko.Observable<boolean> = ko.observable(false);
    maxStations: ko.Observable<number> = ko.observable(null);
    allowDeltas: ko.Computed<boolean>;

    constructor(selections: Selections) {
        this.selections = selections;

        this.singleStation = ko.computed({
            owner: this,
            read: () => {
                return this.selections.type().singleStation;
            }
        });

        this.allowDeltas = ko.computed({
            owner: this,
            read: () => {
                //Need to know the report type
                var t = this.selections.type();
                if (!t) return false;

                //Allow current conditions report
                if (t.value === "CurrentConditions") return true;


                //Also allow Tabular
                if (t.value !== "Tabular") return false;

                //but only for latest observation date type
                var dt = this.selections.dateType();
                return dt && dt.value === ReportDateTypeEnum.LatestObservation;
            }
        });
    }
}

class Choice<T> {
    label: string;
    value: T;

    constructor(label: string, value: T) {
        this.label = label;
        this.value = value;
    }
}

class Month extends Choice<number> {
    days: number;

    constructor(label: string, value: number, days: number) {
        super(label, value);
        this.days = days;
    }
}

class DateType extends Choice<ReportDateTypeEnum> {
    constructor(label: string, value: ReportDateTypeEnum) {
        super(label, value);
    }
}

class DateValues {
    count: ko.Observable<number> = ko.observable(12);

    //Seasonal data
    seasonStartMonth: ko.Observable<Month> = ko.observable();
    seasonStartDay: ko.Observable<number> = ko.observable();
    seasonEndMonth: ko.Observable<Month> = ko.observable();
    seasonEndDay: ko.Observable<number> = ko.observable();

    //custom date range
    rangeStart: ko.Observable<string> = ko.observable();
    rangeEnd: ko.Observable<string> = ko.observable();

    seasonStartDays: ko.Computed<Array<number>> = ko.computed({
        owner: this,
        read: () => {
            var days: number;
            var month = this.seasonStartMonth();
            if (!month) {
                days = 31;
            } else {
                days = month.days;
            }

            var result = new Array(days);
            for (var i = 0; i < days; i++) {
                result[i] = i + 1;
            }

            return result;
        }
    });

    seasonEndDays: ko.Computed<Array<number>> = ko.computed({
        owner: this,
        read: () => {
            var days: number;
            var month = this.seasonEndMonth();
            if (!month) {
                days = 31;
            } else {
                days = month.days;
            }

            var result = new Array(days);
            for (var i = 0; i < days; i++) {
                result[i] = i + 1;
            }

            return result;
        }
    });
}

interface IFieldDepth {
    field: string;
    depth: number;
    isMoisture: boolean;
}

interface IGddModel {
    name: string;
    upper: number;
    lower: number;
    method: string;
    cutoff: string;
    last: number;
}

class GddSettings {
    model: ko.Observable<IGddModel> = ko.observable();

    name: ko.Observable<string> = ko.observable();
    method: ko.Observable<Choice<string>> = ko.observable();
    lowerThreshold: ko.Observable<number> = ko.observable(40);
    upperThreshold: ko.Observable<number> = ko.observable();
    cutoff: ko.Observable<Choice<string>> = ko.observable();
    last: ko.Observable<number> = ko.observable(30);

    constructor() {
        this.method(this.gddMethods[0]);
        this.cutoff(this.gddCutoffs[0]);
        this.model.subscribe(this.onModelSelect);
    }

    onModelSelect = (model: IGddModel) => {
        if (model == null) {
            this.name("");
            return;
        };

        this.name(model.name);
        this.lowerThreshold(model.lower);
        this.upperThreshold(model.upper);
        this.method(this.getMethod(model.method));
        this.cutoff(this.getCutoff(model.cutoff));
    }


    gddMethods = [
        new Choice<string>("Single Sine", "SingleSine"),
        new Choice<string>("Double Sine", "DoubleSine"),
        new Choice<string>("Single Triangle", "SingleTriangle"),
        new Choice<string>("Double Triangle", "DoubleTriangle")
    ];

    getMethod = (value: string) => {
        return ko.utils.arrayFirst(this.gddMethods, (choice: Choice<string>) => choice.value === value);
    };

    gddCutoffs = [
        new Choice<string>("Horizontal", "Horizontal"),
        new Choice<string>("Intermediate", "Intermediate"),
        new Choice<string>("Vertical", "Vertical")
    ];

    getCutoff = (value: string) => {
        return ko.utils.arrayFirst(this.gddCutoffs, (choice: Choice<string>) => choice.value === value);
    };
}

enum ThresholdOperator {
    Below = 0,
    Above = 1
}

class ClimateSummaryThreshold {
    field: ko.Observable<Choice<string>> = ko.observable();
    operator: ko.Observable<ThresholdOperator> = ko.observable();
    value: ko.Observable<number> = ko.observable();

    constructor(field: Choice<string>, operator: ThresholdOperator, value: number) {
        this.field(field);
        this.operator(operator);
        this.value(value);
    }
}

class SoilMoistureDepthOption {
    depth: number;
    wilt: ko.Observable<number> = ko.observable();
    fieldCapacity: ko.Observable<number> = ko.observable();

    constructor(depth: number, wilt: number, fieldCapacity: number) {
        this.depth = depth;
        this.wilt(wilt);
        this.fieldCapacity(fieldCapacity);
    }
}

class ReportType extends Choice<string> {
    singleStation: boolean;
    customSettings: string;
    icon: string;

    constructor(label: string, value: string, singleStation: boolean = false, customSettings: string = null, icon: string = null) {
        super(label, value);
        this.singleStation = singleStation;
        this.customSettings = customSettings;
        this.icon = icon;
    }
}

class Station extends Choice<string> {
    combo: string;

    constructor(label: string, value: string) {
        super(label, value);
        this.combo = value + " | " + label;
    }
}

class Group extends Choice<number> {
    constructor(label: string, value: number) {
        super(label, value);
    }
}

class Field extends Choice<string> {
    combo: string;
    numeric: boolean;

    constructor(label: string, value: string, numeric: boolean) {
        super(label, value);
        this.combo = value + " - " + label;
        this.numeric = numeric;
    }
}

class Selections {
    type: ko.Observable<ReportType> = ko.observable();
    stations: ko.ObservableArray<Station> = ko.observableArray();
    groups: ko.ObservableArray<Group> = ko.observableArray();
    interval: ko.Observable<Choice<number>> = ko.observable();
    fields: ko.ObservableArray<Field> = ko.observableArray();
    deltaFields: ko.ObservableArray<Field> = ko.observableArray();
    dateType: ko.Observable<DateType> = ko.observable();
    dateValues = new DateValues();
    gddSettings = new GddSettings();

    /* Report specific */
    climateSummaryThresholds: ko.ObservableArray<ClimateSummaryThreshold> = ko.observableArray();

    soilMoistureDepths: ko.ObservableArray<SoilMoistureDepthOption> = ko.observableArray();
    
    stationsGroupsCount: ko.Computed<number> = ko.pureComputed({
        owner: this,
        read: () => {
            if (this.type().singleStation) {
                return this.stations().length;
            }
            return this.stations().length + this.groups().length;
        }
    });

    constructor(reportType: ReportType) {
        this.type = ko.observable(reportType);
    }

    addStation = (station: Station) => {
        this.stations.push(station);
    }

    removeStation = (station: Station) => {
        this.stations.remove(station);
    }

    addGroup = (group: Group) => {
        this.groups.push(group);
    }

    removeGroup = (group: Group) => {
        this.groups.remove(group);
    }

    removeField = (field: Field) => {
        this.fields.remove(field);
        this.deltaFields.remove(field);
    }

    removeDelta = (field: Field) => {
        this.deltaFields.remove(field);
    }

    removeThreshold = (threshold: ClimateSummaryThreshold) => {
        this.climateSummaryThresholds.remove(threshold);
    }
    
}

class Result {
    reportType: string;
    stations: string;
    groups: string;
    interval: number;
    fields: string;
    deltas: string;

    dateType: ReportDateTypeEnum;

    dateCount: number;

    dateStartMonth: number;
    dateStartDay: number;
    dateStartYear: number;

    dateEndMonth: number;
    dateEndDay: number;
    dateEndYear: number;

    //Climate summary
    thresholds: any;

    //Degree Day
    gdd: any;

    // Soil Moisture 
    soilMoistureDepths: any;
}

class ReportBuilderModel {
    isLoading: ko.Observable<boolean> = ko.observable(false);

    currentStep: ko.Observable<BuilderStep> = ko.observable(BuilderStep.Stations);

    months = [new Month("January", 1, 31), new Month("February", 2, 28), new Month("March", 3, 31), new Month("April", 4, 30),
              new Month("May", 5, 31), new Month("June", 6, 30), new Month("July", 7, 31), new Month("August", 8, 31),
              new Month("September", 9, 30), new Month("October", 10, 31), new Month("November", 11, 30), new Month("December", 12, 31)];

    //TODO: Use API provided field options
    climateSummaryFields = [
        new Choice<string>("Min Temp (°F)", "TempMinDay"),
        new Choice<string>("Max Temp (°F)", "TempMaxDay"),
        new Choice<string>("Precipitation (in)", "PrecipDay"),
        new Choice<string>("Max Wind Gust (mph)", "WindMaxDay")
    ];
    
    gddModels: ko.ObservableArray<IGddModel> = ko.observableArray();

    selections: Selections;
    selectedStation: ko.Observable<Station> = ko.observable();
    stationFilter: ko.Observable<string> = ko.observable("");
    groupFilter: ko.Observable<string> = ko.observable("");
    selectedField: ko.Observable<Field> = ko.observable();
    selectedDelta: ko.Observable<Field> = ko.observable();

    types: ko.ObservableArray<ReportType> = ko.observableArray();
    stations: ko.ObservableArray<Station> = ko.observableArray();
    groups: ko.ObservableArray<Group> = ko.observableArray();
    intervals: ko.ObservableArray<Choice<number>> = ko.observableArray();
    fields: ko.ObservableArray<Field> = ko.observableArray();
    dateTypes: ko.ObservableArray<DateType> = ko.observableArray();

    constraints: Constraints;

    availableStations: ko.Computed<Station[]>;
    availableGroups: ko.Computed<Group[]>;
    availableFields: ko.Computed<Field[]>;
    availableDeltas: ko.Computed<Field[]>;

    canSelectFields: ko.Computed<boolean>;
    canSubmit: ko.Computed<boolean>;
    result: ko.Computed<Result>;
    resultJson: ko.Computed<string>;

    editId: string;
    ownerId: string;

    selectedDepths: ko.Computed<SoilMoistureDepthOption[]>;

    constructor(initialData: Result, reportTypes: Array<ReportType>, selectedType: ReportType, ownerId: string, editId: string) {

        this.ownerId = ownerId;
        this.editId = editId;
        this.types(reportTypes);

        var initialStations: Array<string> = null;
        var initialGroups: Array<number> = null;

        if (initialData && initialData.stations) {
            initialStations = initialData.stations.split(",");
        }

        if (initialData && initialData.groups) {
            initialGroups = initialData.groups.split(",").map(g => parseInt(g));
        }


        this.selections = new Selections(selectedType);
        this.selectedStation.subscribe(this.onStationSelect);
        this.selectedField.subscribe(this.onFieldSelect);
        this.selectedDelta.subscribe(this.onDeltaSelect);

        this.selections.type.subscribe(this.onReportTypeSelect);
        this.selections.interval.subscribe(this.onIntervalSelect);
        this.selections.dateType.subscribe(this.onDateTypeSelect);
        this.selections.stations.subscribe(this.onSelectedStationsChange);
        this.selections.groups.subscribe(this.onSelectedGroupsChange);

        this.selections.dateValues.seasonEndMonth(this.months[11]);
        this.selections.dateValues.seasonEndDay(31);

        this.constraints = new Constraints(this.selections);

        //Configure default climate summary threshold options
        this.selections.climateSummaryThresholds.push(new ClimateSummaryThreshold(this.climateSummaryFields[0], ThresholdOperator.Below, 28));
        this.selections.climateSummaryThresholds.push(new ClimateSummaryThreshold(this.climateSummaryFields[0], ThresholdOperator.Below, 32));
        this.selections.climateSummaryThresholds.push(new ClimateSummaryThreshold(this.climateSummaryFields[1], ThresholdOperator.Above, 90));
        this.selections.climateSummaryThresholds.push(new ClimateSummaryThreshold(this.climateSummaryFields[1], ThresholdOperator.Above, 100));
        this.selections.climateSummaryThresholds.push(new ClimateSummaryThreshold(this.climateSummaryFields[2], ThresholdOperator.Above, 0.01));
        this.selections.climateSummaryThresholds.push(new ClimateSummaryThreshold(this.climateSummaryFields[2], ThresholdOperator.Above, 0.10));

        // Configure default soil moisture depth options
        var selections1 = this.selections;
        if (typeof fieldDepthMap !== 'undefined') {
            const fieldDepths: Set<number> = new Set(fieldDepthMap.map(item => item.depth).sort((a, b) => a - b));
            fieldDepths.forEach(function(depth) {
                selections1.soilMoistureDepths.push(new SoilMoistureDepthOption(depth, null, null));
            });
        }
        
        if (initialData && initialData.thresholds) {
            /* Climate Summary report thresholds */
            this.selections.climateSummaryThresholds.removeAll();
            var thresholdsJs = JSON.parse(initialData.thresholds);
            for (var i = 0; i < thresholdsJs.length; i++) {
                var field = ko.utils.arrayFirst(this.climateSummaryFields, item => item.value === thresholdsJs[i].f);
                var operator = thresholdsJs[i].o ? ThresholdOperator.Above : ThresholdOperator.Below;
                if (field) {
                    this.selections.climateSummaryThresholds.push(new ClimateSummaryThreshold(field, operator, thresholdsJs[i].v));
                }
            }
        }

        if (initialData && initialData.gdd) {
            /* Degree Day report settings */
            var gdd = JSON.parse(initialData.gdd);
            this.selections.gddSettings.name(gdd.name);
            this.selections.gddSettings.lowerThreshold(gdd.lowerThreshold);
            this.selections.gddSettings.upperThreshold(gdd.upperThreshold);
            this.selections.gddSettings.method(this.selections.gddSettings.getMethod(gdd.method));
            this.selections.gddSettings.cutoff(this.selections.gddSettings.getCutoff(gdd.cutoff));
            this.selections.gddSettings.last(gdd.last);
        }

        if (initialData && initialData.soilMoistureDepths) {
            /* Soil Moisture Depth Options */
            var soilMoistureDepthsJs = JSON.parse(initialData.soilMoistureDepths);
            for (var i = 0; i < soilMoistureDepthsJs.length; i++) {
                var selectionIndex = this.selections.soilMoistureDepths().findIndex((depth, index) => {
                    return depth.depth == soilMoistureDepthsJs[i].depth;
                    //if (depth.depth == soilMoistureDepthsJs[i].depth) return true;
                });
                if (selectionIndex >= 0) {
                    this.selections.soilMoistureDepths.replace(
                        this.selections.soilMoistureDepths()[selectionIndex], new SoilMoistureDepthOption(
                        soilMoistureDepthsJs[i].depth,
                        soilMoistureDepthsJs[i].wilt,
                        soilMoistureDepthsJs[i].fieldCapacity));
                }
            }
        }
        
        this.selectedDepths = ko.computed({
            owner: this,
            read: () => {
                return ko.utils.arrayFilter(this.selections.soilMoistureDepths(),
                    (depthOption: SoilMoistureDepthOption): boolean => {
                        var selectedFields = this.selections.fields();
                        for (var i = 0; i < selectedFields.length; i++) {
                            if (fieldDepthMap.findIndex((fieldDepth, index) => {
                                return fieldDepth.field == selectedFields[i].value && fieldDepth.depth == depthOption.depth;
                                /*if (fieldDepth.field == selectedFields[i].value && fieldDepth.depth == depthOption.depth )
                                    return true;*/
                            })>=0) return true;
                        }
                        return false;
                    });
            }
        });
        
        this.availableStations = ko.computed({
            owner: this,
            read: () => {
                var filter = (this.stationFilter() || "").toLowerCase();

                var list = this.constraints.singleStation()
                    ? this.stations()
                    : ko.utils.arrayFilter(this.stations(),
                        (station: Station): boolean => {
                            var selectedStations = this.selections.stations();
                            for (var i = 0; i < selectedStations.length; i++) {
                                if (selectedStations[i].value == station.value) return false;
                            }
                            return true;
                        });

                if (!filter) return list;
                return ko.utils.arrayFilter(list, (s: Station) => s.combo.toLowerCase().indexOf(filter) >= 0);
            }
        });

        this.availableGroups = ko.computed({
            owner: this,
            read: () => {
                //No groups can be selected for single station mode
                if (this.constraints.singleStation()) return [];

                var filter = (this.groupFilter() || "").toLowerCase();

                //Otherwise filter the list by determining if the group is already in the selected groups array
                var list = ko.utils.arrayFilter(this.groups(),
                    (group: Group): boolean => {
                        var selectedGroups = this.selections.groups();
                        for (var i = 0; i < selectedGroups.length; i++) {
                            if (selectedGroups[i].value == group.value) return false;
                        }

                        return true;
                    });

                if (!filter) return list;
                return ko.utils.arrayFilter(list, (g: Group) => g.label.toLowerCase().indexOf(filter) >= 0);
            }
        });

        this.availableFields = ko.computed({
            owner: this,
            read: () => {
                //Simply return all if in single-select mode
                if (this.constraints.singleField()) return this.fields();

                //Otherwise filter the list by determining if the station is already in the selected stations array
                return ko.utils.arrayFilter(this.fields(),
                    (field: Field): boolean => {
                        var selectedFields = this.selections.fields();
                        for (var i = 0; i < selectedFields.length; i++) {
                            if (selectedFields[i].value == field.value) return false;
                        }

                        return true;
                    });
            }
        });

        this.availableDeltas = ko.computed({
            owner: this,
            read: () => {
                if (!this.constraints.allowDeltas()) return [];

                var deltas = this.selections.fields().length
                    ? this.selections.fields()
                    : this.availableFields();

                return ko.utils.arrayFilter(deltas, (delta: Field): boolean => {
                    if (!delta.numeric) return false;
                    var selectedDeltas = this.selections.deltaFields();
                        
                        for (var i = 0; i < selectedDeltas.length; i++) {
                            if (selectedDeltas[i].value == delta.value) return false;
                        }

                        return true;
                    });
            }
        });

        this.canSelectFields = ko.pureComputed({
            owner: this,
            read: () => {
                if (this.currentStep() < 4) return false;

                var customSettings = this.selections.type().customSettings;
                if (customSettings === "thresholds") return false;
                if (customSettings === "gdd") return false;
                if (customSettings === "windrose") return false;

                return true;
            }
        })

        this.canSubmit = ko.computed({
            owner: this,
            read: () => {
                if (this.currentStep() < 4) return false;

                if (this.constraints.singleField()) {
                    return this.selections.fields().length === 1;
                }

                if (this.constraints.allowAllFields()) {
                    return true;
                }

                return this.selections.fields().length > 0;
            }
        });

        this.result = ko.computed({
            owner: this,
            read: () => {
                var result = new Result();
                result.reportType = this.selections.type().value;
                result.stations = this.selections.stations().map(station => station.value).join(",");

                if (!this.selections.type().singleStation) {
                    result.groups = this.selections.groups().map(group => group.value).join(",");
                }

                var interval = this.selections.interval();
                if (interval && interval.value) {
                    result.interval = interval.value;
                }

                result.fields = this.selections.fields().map(field => field.value).join(",");

                var dateType = this.selections.dateType();
                if (dateType) {
                    result.dateType = dateType.value;

                    if (dateType.value === ReportDateTypeEnum.Season) {
                        result.dateStartDay = this.selections.dateValues.seasonStartDay();
                        result.dateEndDay = this.selections.dateValues.seasonEndDay();

                        var startMonth = this.selections.dateValues.seasonStartMonth();
                        var endMonth = this.selections.dateValues.seasonEndMonth();

                        if (startMonth) result.dateStartMonth = startMonth.value;
                        if (endMonth) result.dateEndMonth = endMonth.value;
                    } else if (dateType.value === ReportDateTypeEnum.Count) {
                        result.dateCount = this.selections.dateValues.count();
                    } else if (dateType.value === ReportDateTypeEnum.DateRange) {
                        try {
                            var start = this.selections.dateValues.rangeStart().split('/');
                            var end = this.selections.dateValues.rangeEnd().split('/');

                            result.dateStartMonth = parseInt(start[0]);
                            result.dateStartDay = parseInt(start[1]);
                            result.dateStartYear = parseInt(start[2]);

                            result.dateEndMonth = parseInt(end[0]);
                            result.dateEndDay = parseInt(end[1]);
                            result.dateEndYear = parseInt(end[2]);
                        } catch (e) {
                            //do nothing
                        } 
                    }
                }

                if (this.selections.type().customSettings === "thresholds") {
                    result.thresholds = ko.toJSON(this.selections.climateSummaryThresholds().map(x => ({
                        f: x.field().value,
                        o: x.operator(),
                        v: x.value()
                    })));
                }

                if (this.selections.type().customSettings === "gdd") {
                    result.gdd = ko.toJSON({
                        name: this.selections.gddSettings.name(),
                        lowerThreshold: this.selections.gddSettings.lowerThreshold(),
                        upperThreshold: this.selections.gddSettings.upperThreshold(),
                        method: this.selections.gddSettings.method().value,
                        cutoff: this.selections.gddSettings.cutoff().value,
                        last: this.selections.stations().length === 1 ? this.selections.gddSettings.last() : null
                    });
                }

                if (this.selections.type().customSettings === "soilmoisture") {
                    result.soilMoistureDepths = ko.toJSON(this.selectedDepths().map(x => ({
                        depth: x.depth,
                        wilt: x.wilt(),
                        fieldCapacity: x.fieldCapacity()
                    })));
                }

                if (this.constraints.allowDeltas()) {
                    result.deltas = this.selections.deltaFields().map(delta => delta.value).join(",");
                }

                setResultToQueryString(result);
                return result;
            }
        });

        this.resultJson = ko.computed({
            owner: this,
            read: () => {
                var json = ko.toJSON(this.result());
                return json;
            }
        });


        //INITIAL LOAD BEHAVIOR ONLY
        var afterDateType = () => {

            if (initialData.dateType === ReportDateTypeEnum.Count) {
                this.selections.dateValues.count(initialData.dateCount);
            } else if (initialData.dateType === ReportDateTypeEnum.DateRange) {
                if (initialData.dateStartMonth && initialData.dateStartDay && initialData.dateStartYear) {
                    this.selections.dateValues.rangeStart(`${initialData.dateStartMonth}/${initialData.dateStartDay}/${initialData.dateStartYear}`);
                }
                if (initialData.dateEndMonth && initialData.dateEndDay && initialData.dateEndYear) {
                    this.selections.dateValues.rangeEnd(`${initialData.dateEndMonth}/${initialData.dateEndDay}/${initialData.dateEndYear}`);
                }
            } else if (initialData.dateType === ReportDateTypeEnum.Season) {
                this.selections.dateValues.seasonStartMonth(this.months.find(i => i.value === initialData.dateStartMonth));
                this.selections.dateValues.seasonStartDay(initialData.dateStartDay);
                this.selections.dateValues.seasonEndMonth(this.months.find(i => i.value === initialData.dateEndMonth));
                this.selections.dateValues.seasonEndDay(initialData.dateEndDay);
            }


            if (!initialData || !initialData.fields) return;
            var fieldsArray = initialData.fields.split(",");

            if (initialData.deltas) {
                this.loadFields(fieldsArray, initialData.deltas.split(","));
            } else {
                this.loadFields(fieldsArray, null);
            }
        }

        var afterIntervals = () => {
            if (initialData && initialData.dateType > 0) {
                this.loadDateTypes(initialData.dateType, afterDateType);
            }
        };

        var afterGroups = () => {
            if (initialData && initialData.interval) {
                this.loadIntervals(initialData.interval, afterIntervals);
            }
        };

        var afterStations = () => {
            this.loadGroups(initialGroups, afterGroups);
        };

        //TODO: only load gdd models if needed
        this.loadGddModels();

        //Trigger the initial data load
        this.loadStations(initialStations, afterStations);
    }

    addThreshold = () => {
        this.selections.climateSummaryThresholds.push(new ClimateSummaryThreshold(this.climateSummaryFields[0], ThresholdOperator.Below, 32));
    }

    onReportTypeSelect = (reportType: ReportType) => {

        if (reportType.value === "Comparison") {
            window.location.href = this.ownerId
                ? `/reports/build/comparison/onbehalf/${this.ownerId}`
                : "/reports/build/comparison";
            return;
        }       

        this.currentStep(BuilderStep.Stations);

        if (reportType.singleStation && this.selections.stations().length > 0) {
            var station = this.selections.stations()[0];
            this.selections.stations.removeAll();
            this.selectedStation(station);
            this.selections.stations.push(station);
        }
    }

    onStationSelect = (station: Station) => {
        if (!station) return;

        if (this.constraints.singleStation()) {
            this.selections.stations.removeAll();
            this.selections.stations.push(station);
        } 

        //Reset the next step (which will in turn reset the following steps)
        this.selections.interval(null);
        this.currentStep(BuilderStep.Stations);
    }

    onGroupSelect = (group: Group) => {
        if (!group) return;

        //Reset the next steps to the stations step
        this.selections.interval(null);
        this.currentStep(BuilderStep.Stations);
    }

    onSelectedStationsChange = () => {
        //Reset the next step (which will in turn reset the following steps)
        this.selections.interval(null);
        this.currentStep(BuilderStep.Stations);
    }

    onSelectedGroupsChange = () => {
        //Reset the next step (which will in turn reset the following steps)
        this.selections.interval(null);
        this.currentStep(BuilderStep.Stations);
    }

    onIntervalSelect = (interval: Choice<number>) => {
        this.selections.dateType(null);

        if (!interval) {
            return;
        } 
        
        this.loadDateTypes();
    }

    onDateTypeSelect = (dateType: DateType) => {
        this.selections.fields.removeAll();
        this.selections.deltaFields.removeAll();
        this.currentStep(BuilderStep.Date);

        if (!dateType) {
            return;
        }
    }

    onFieldSelect = (field: Field) => {
        if (!field) {
            return;
        };

        if (this.constraints.singleField()) {
            //If single field mode, remove all from selected fields list
            this.selections.fields.removeAll();
        } else {
            //If multi-field mode, unselect the field since we auto-add to the collection
            this.selectedField(null);
        }

        //Finally add the field to the selected fields list
        this.selections.fields.push(field);
    }

    onDeltaSelect = (delta: Field) => {
        if (!delta) {
            return;
        };

        //unselect the field since we auto-add to the collection
        this.selectedDelta(null);

        //Finally add the field to the selected fields list
        this.selections.deltaFields.push(delta);
    }


    loadStations = (selected: string[], onSuccess?: () => void) => {
        var model = this;
        model.stations.removeAll();
        model.isLoading(true);

        $.ajax("/reportbuilderapi/stations?oid=" + this.ownerId,
        {
            success: function (data) {
                var stations = new Array<Station>();

                for (var i = 0; i < data.length; i++) {
                    var item = data[i];
                    stations.push(new Station(item.label, item.value));
                }

                model.stations(stations);

                // Restore selections in SAVED order, not API list order
                var selections = new Array<Station>();
                if (selected) {
                    for (var j = 0; j < selected.length; j++) {
                        var match = ko.utils.arrayFirst(stations, (s: Station) => s.value === selected[j]);
                        if (match) selections.push(match);
                    }
                }
                model.selections.stations(selections);

                if (model.constraints.singleStation() && model.selections.stations().length == 1) {
                    model.selectedStation(model.selections.stations()[0]);
                }

                if (onSuccess) onSuccess();
            },
            complete: function() {
                model.isLoading(false);
            }
        });
    }

    loadGroups = (selected: number[], onSuccess?: () => void) => {
        var model = this;
        model.groups.removeAll();
        model.isLoading(true);

        $.ajax("/reportbuilderapi/groups?oid=" + this.ownerId,
            {
                success: function (data) {
                    if (data) {
                        var groups = new Array<Group>();

                        for (var i = 0; i < data.length; i++) {
                            var item = data[i];
                            groups.push(new Group(item.label, item.value));
                        }

                        model.groups(groups);

                        // Restore selections in SAVED order, not API list order
                        var selections = new Array<Group>();
                        if (!model.constraints.singleStation() && selected) {
                            for (var j = 0; j < selected.length; j++) {
                                var match = ko.utils.arrayFirst(groups, (g: Group) => g.value === selected[j]);
                                if (match) selections.push(match);
                            }
                        }
                        model.selections.groups(selections);
                    }

                    if (onSuccess) onSuccess();
                },
                complete: function () {
                    model.isLoading(false);
                }
            });
    }

    loadIntervals = (selected?: number, onSuccess?: () => void) => {
        var model = this;
        var r = this.result();
        var data = {
            reportType: r.reportType,
            stationIds: r.stations,
            groupIds: r.groups
        }
        model.isLoading(true);
        $.ajax("/reportbuilderapi/intervals?oid=" + this.ownerId,
        {
            data: data,
            method: "post",
            success: function(data) {
                //Clear existing intervals
                model.intervals.removeAll();
                //Remove selection
                model.selections.interval(null);

                var intervals = new Array<Choice<number>>();
                var selection: Choice<number>;

                for (var i = 0; i < data.length; i++) {
                    var item = data[i];
                    var interval = new Choice<number>(item.label, item.value);
                    intervals.push(interval);

                    if (interval.value === selected) {
                        selection = interval;
                    }
                }

                model.intervals(intervals);

                if (selection) {
                    model.selections.interval(selection);
                }

                //Change step which will update UI accordingly
                model.currentStep(BuilderStep.Interval);

                if (onSuccess) onSuccess();

            },
            complete: function() {
                model.isLoading(false);
            }
            //TODO: Handle errors
        });
    }

    loadDateTypes = (selected?: ReportDateTypeEnum, onSuccess?: () => void) => {
        if (this.selections.type().customSettings === "nodate") {
            if (onSuccess) { onSuccess(); }
            else { this.loadFields();}
            return;
        }

        var model = this;
        var r = this.result();
        var data = {
            reportType: this.selections.type().value,
            stationIds: r.stations,
            groupIds: r.groups,
            interval: this.selections.interval().value
        }
        model.isLoading(true);
        $.ajax("/reportbuilderapi/datetypes?oid=" + this.ownerId,
            {
                data: data,
                method: "post",
                success: function (data) {
                    //Remove any existing choices and selections
                    model.dateTypes.removeAll();
                    model.selections.dateType(null);

                    //Change step which will update UI accordingly
                    model.currentStep(BuilderStep.Date);

                    var dateTypes = new Array<DateType>();
                    var selection:DateType;

                    for (var i = 0; i < data.length; i++) {
                        var item = data[i];
                        var dateType = new DateType(item.label, (<any>ReportDateTypeEnum)[item.value]);
                        dateTypes.push(dateType);

                        if (dateType.value === selected) {
                            selection = dateType;
                        }
                    }

                    model.dateTypes(dateTypes);
                    if (selection) {
                        model.selections.dateType(selection);
                    }
                    

                    if (onSuccess) onSuccess();
                },
                complete: function () {
                    model.isLoading(false);
                }
                //TODO: Handle errors
            });
    }

    loadFields = (selected?:Array<string>, deltas?:Array<string>) => {
        var model = this;
        var r = this.result();
        var data = {
            reportType: this.selections.type().value,
            stationIds: r.stations,
            groupIds: r.groups,
            interval: this.selections.interval().value
        }
        model.isLoading(true);
        $.ajax("/reportbuilderapi/fields?oid=" + this.ownerId,
            {
                data: data,
                method: "post",
                success: function (data) {
                    //Remove any existing choices and selections
                    model.fields.removeAll();
                    model.selections.fields.removeAll();

                    model.constraints.singleField(data.singleField);
                    model.constraints.allowAllFields(data.allowAllFields);

                    var fields = new Array<Field>();

                    //Add returns results as fields to model
                    for (var i = 0; i < data.choices.length; i++) {
                        let item = data.choices[i];
                        let field = new Field(item.label, item.value, item.numeric);
                        fields.push(field);
                    }
                    
                    model.fields(fields);

                    //pre-select any specified fields
                    if (selected) {
                        var selections = new Array<Field>();

                        for (var i = 0; i < selected.length; i++) {
                            let fieldVal = selected[i];
                            let field = ko.utils.arrayFirst(model.fields(), (f: Field) => {
                                return f.value === fieldVal;
                            });

                            if (field) {
                                selections.push(field);
                            }
                        }

                        model.selections.fields(selections);
                    }


                    //pre-select any specified delta fields
                    if (deltas) {
                        var deltaSelections = new Array<Field>();


                        for (var i = 0; i < deltas.length; i++) {
                            let deltaVal = deltas[i];
                            let delta = ko.utils.arrayFirst(model.fields(), (f: Field) => {
                                return f.value === deltaVal;
                            });

                            if (delta) {
                                deltaSelections.push(delta);
                            }
                        }

                        model.selections.deltaFields(deltaSelections);
                    }



                    //Change step which will update UI accordingly
                    model.currentStep(BuilderStep.Fields);

                },
                complete: function () {
                    model.isLoading(false);
                }
                //TODO: Handle errors
            });
    }

    loadGddModels = () => {
        var model = this;

        $.ajax("/reportbuilderapi/gddmodels",
            {
                method: "get",
                success: function (data) {
                    model.gddModels(data);
                    
                    var name = model.selections.gddSettings.name();
                    if (name) {
                        /* select the gdd model drop down, if a name is provided */
                        var m = ko.utils.arrayFirst(model.gddModels(), (model: IGddModel) => model.name === name);
                        if (m) {
                            model.selections.gddSettings.model(m);
                        }
                    }
                },
                complete: function () {
                }
            });
    }

    continueAfterStations = () => {
        if (this.selections.type().value === "SoilMoistureChart") {
            this.loadIntervals(60);
        } else {
            this.loadIntervals();
        }
    }

    continueAfterDate = () => {

        if (this.selections.type().value == "SoilMoistureChart") {
            // Automatically select all moisture fields and the IrrPressure, IrrPressure2, PrecipDay fields for the Soil Moisture Chart
            var moistureFieldDepths = ko.utils.arrayFilter(fieldDepthMap,
                (fieldDepth: IFieldDepth): boolean => {
                    return fieldDepth.isMoisture;
                });
            var selectedFields: string[] = Array.from(new Set(moistureFieldDepths.map(item => item.field)));
            selectedFields.push("IrrPressure");
            selectedFields.push("IrrPressure2");
            selectedFields.push("PrecipDay");
            this.loadFields(selectedFields, null);
        } else {
            this.loadFields();
        }
        //this.currentStep(BuilderStep.Fields);
    }

    continueAfterFields = () => {
        var editSuffix = "";
        if (this.editId) {
            editSuffix = "&edit=" + this.editId;
        }

        if (this.ownerId) {
            window.location.href = `/reports/view/onbehalf/${this.ownerId}?${jQuery.param(ko.toJS(this.result()))}${editSuffix}`;
            return;
        }

        window.location.href = `/reports/view?${jQuery.param(ko.toJS(this.result()))}${editSuffix}`;
    }
}

function getResultFromQueryString(): Result {
    try {
        var json64 = qs("r");
        if (!json64) return null;

        //Convert from the encoded base64 format
        var json = atob(json64);

        //Parse into the original result format
        let result: Result = JSON.parse(json);

        if (result.dateType && typeof (result.dateType) == "string") {
            //Parse string value of date type
            result.dateType = (<any>ReportDateTypeEnum)[result.dateType];
        }

        return result;
    } catch (e) {
        //Do nothing
        //This operation is not critical, and there is high likelihood the string is in invalid format
        //If it matches, we'll use that data to restore the form

        return null;
    }
}

function setResultToQueryString(result: Result) {
    /* THIS FEATURE IS DISABLED FOR THE TIME BEING */

    /*var js = ko.toJSON(result);
    history.replaceState(undefined, undefined, "?r=" + btoa(js));*/
}

function qs(key:string) {
    key = key.replace(/[*+?^$.\[\]{}()|\\\/]/g, "\\$&"); // escape RegEx meta chars
    var match = location.search.match(new RegExp("[?&]" + key + "=([^&]+)(&|$)"));
    return match && decodeURIComponent(match[1].replace(/\+/g, " "));
}

$(function () {

    var editId = $("#ReportBuilder").data("editid");
    var ownerId = $("#ReportBuilder").data("ownerid");
    var initialData = getResultFromQueryString();

    $.ajax("/reportbuilderapi/types",
        {
            success: function (data) {
                var reportTypes: ReportType[] = new Array<ReportType>();
                var selectedType: ReportType;

                for (var i = 0; i < data.length; i++) {
                    var item = data[i];
                    var type = new ReportType(item.label, item.value, item.singleStation, item.customSettings, item.icon);
                    reportTypes.push(type);

                    //If initial data was loaded, check if this type was specified, and set it as selected
                    if (initialData != null && initialData.reportType == type.value) {
                        selectedType = type;
                    }
                }

                if (!selectedType) {
                    //If no report type was selected, select the first one
                    selectedType = reportTypes[0];
                }

                if (selectedType && selectedType.value === "Comparison") {
                    window.location.href = "/reports/build/comparison" + window.location.search;
                    return;
                }

                ko.applyBindings(new ReportBuilderModel(initialData, reportTypes, selectedType, ownerId, editId), document.getElementById('ReportBuilder'));
            }
        });
});